import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { logNavigation } from './utils/performanceLogger';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';

// Context Providers
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TutorialProvider } from './contexts/TutorialContext';

// Layout Components
import Layout from './components/Layout/Layout';
import PublicLayout from './components/Layout/PublicLayout';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';

// Pages
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import TenantSelection from './pages/Auth/TenantSelection';
import ResetPassword from './pages/Auth/ResetPassword';
import LineGroupVerified from './services/LineGroupVerified';
import Dashboard from './apps/Dashboard/Dashboard';
import VisStudio from './apps/Dashboard/VisStudio';
import Customers from './entities/Customers/Customers';
import CustomerDetail from './entities/Customers/CustomerDetail';
import CustomerForm from './entities/Customers/CustomerForm';
import Leads from './entities/Leads/Leads';
import LeadDetail from './entities/Leads/LeadDetail';
import LeadForm from './entities/Leads/LeadForm';
import LeadGen from './entities/Leads/LeadGen';
import Opportunities from './entities/Opportunities/Opportunities';
import OpportunityDetail from './entities/Opportunities/OpportunityDetail';
import OpportunityForm from './entities/Opportunities/OpportunityForm';
import Pipeline from './apps/Pipeline/Pipeline';
import SalesTargetList from './pages/SalesTargets/SalesTargetList';
import SalesTargetDetail from './pages/SalesTargets/SalesTargetDetail';
import SalesTargetForm from './pages/SalesTargets/SalesTargetForm';
import SalesTargetReports from './pages/SalesTargets/SalesTargetReports';
import SalesTargetDashboard from './pages/SalesTargets/SalesTargetDashboard';
import Activities from './entities/Activities/Activities';
import Settings from './pages/Admin/Settings/Settings';
import AIInsights from './pages/AIInsights/AIInsights';
import BusinessCardScanner from './apps/BusinessCardScanner/BusinessCardScanner';
import Attachments from './pages/Attachments/Attachments';
import TagManager from './pages/Admin/TagManager/TagManager';
import Notes from './entities/Notes/Notes';
import UserProfile from './pages/UserProfile/UserProfile';
import GoogleCalendarCallback from './pages/GoogleCalendarCallback/GoogleCalendarCallback';
import KnowledgeCenter from './entities/KnowledgeCenter/KnowledgeCenter';
import SalesAssist from './apps/SalesAssist/SalesAssist';
import AIManagement from './pages/Admin/AIManagement/AIManagement';
import AIModelManagement from './pages/Admin/GlobalAdmin/GlobalConfiguration/AIModelManagement';
import LineManagement from './pages/Admin/LineManagement/LineManagement';
import Automation from './apps/Automation/Automation';
import SupportTickets from './pages/SupportTickets/SupportTickets';
import OpportunitiesSingleFetch from './pages/PerfTest/OpportunitiesSingleFetch';
import ThemeSettingsPage from './pages/Admin/Settings/ThemeSettings';
import TooltipExamples from './components/Tutorial/TooltipExamples';
import Reports from './apps/Reports/Reports';
import ReportBuilder from './apps/Reports/ReportBuilder';
import ReportViewer from './apps/Reports/ReportViewer';
import VisualizationStudio from './apps/VisualizationStudio/VisualizationStudio';
import VisualizationBuilder from './apps/VisualizationStudio/VisualizationBuilder';

// Admin Components
import AdminDashboard from './pages/Admin/GlobalAdmin/AdminDashboard';
import TenantSettings from './pages/Admin/TenantManagement/TenantSettings';
import TenantManagement from './pages/Admin/TenantManagement/TenantManagement';
import TenantConfiguration from './pages/Admin/TenantManagement/TenantConfiguration';
import TenantEntityConfig from './pages/Admin/TenantManagement/TenantEntityConfig';
import PipelineStudio from './pages/Admin/PipelineStudio/PipelineStudio';
import WebFormManagement from './pages/Admin/WebForm/WebFormManagement';
import CustomFieldManager from './pages/Admin/EntityConfiguration/CustomFieldManager';
import SelectionValuesManagement from './pages/Admin/TenantManagement/SelectionValuesManagement';
import ListingConfigManager from './pages/Admin/TenantAdmin/ListingConfigManager';
import RoleConfigManager from './pages/Admin/TenantAdmin/RoleConfigManager';
import DataGovernanceManager from './pages/Admin/DataGovernance/DataGovernanceManager';
import EntityEntitlementManager from './pages/Admin/GlobalAdmin/EntityEntitlementManagement/EntityEntitlementManager';
import EmailManagement from './pages/Admin/EmailManagement/EmailManagement';
import EntityRelationsTest from './pages/Admin/SystemMonitoring/EntityRelationsTest';
import OCRManagement from './pages/Admin/GlobalAdmin/SystemMonitoring/OCRManagement';
import ClassificationManagement from './pages/Admin/GlobalAdmin/SystemMonitoring/ClassificationManagement';
import MenuConfigManager from './pages/Admin/MenuConfiguration/MenuConfigManager';
import PackageManagement from './pages/Admin/GlobalAdmin/PackageManagement/PackageManagement';
import TenantUsageDashboard from './pages/Admin/GlobalAdmin/TenantUsageDashboard/TenantUsageDashboard';
import FieldVisibilityManager from './pages/Admin/FieldVisibilityManager';

// Repository Components
import RepositorySelector from './components/UI/RepositorySelector';
import NoTenantAccess from './components/UI/NoTenantAccess';
import TenantChangeWarning from './components/TenantChangeWarning/TenantChangeWarning';
import { useTenantChangeDetection } from './hooks/useTenantChangeDetection';
import { clearConfigCache } from './utils/entityConfig';

// Create a client for React Query
// staleTime: avoid immediate refetch when switching entity pages (reduces slowdown after 5–6 navigations)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000, // 1 min – data stays fresh, no refetch on remount when navigating back
      cacheTime: 5 * 60 * 1000, // 5 min – keep unused data in cache (react-query v3)
    },
  },
});

// Logs navigation to console when REACT_APP_PERF_LOG=1 (for bottleneck debugging)
const NavigationPerfLogger = () => {
  const location = useLocation();
  useEffect(() => {
    logNavigation(location.pathname);
  }, [location.pathname]);
  return null;
};

// Clear entity config cache on tenant switch to avoid stale data and limit cache growth
const TenantSwitchCacheClear = () => {
  useEffect(() => {
    const handler = () => clearConfigCache();
    window.addEventListener('tenant:afterSwitch', handler);
    return () => window.removeEventListener('tenant:afterSwitch', handler);
  }, []);
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user is Support role trying to access restricted routes
  const isSupportRole = user.roles?.some(role => (role.RoleName || role) === 'Support') || 
                       user.selectedRole?.RoleName === 'Support' || 
                       user.selectedRole === 'Support';
  
  if (isSupportRole) {
    // Support role can only access support-tickets and knowledge-center
    const allowedPaths = ['/support-tickets', '/knowledge-center', '/admin/entities/knowledge'];
    const currentPath = location.pathname;
    const isAllowedPath = allowedPaths.some(path => currentPath.startsWith(path));
    
    if (!isAllowedPath) {
      return <Navigate to="/support-tickets" replace />;
    }
  }
  
  // Note: User role access is controlled by backend data governance policies
  // User role can access entities with Policy 2/3, but not Policy 1
  // The backend will enforce these restrictions via data governance service
  
  // If user is admin, redirect to admin dashboard
  if (user.userType === 'admin') {
    return <Navigate to="/admin" replace />;
  }
  
  // If user has no tenant access, show the NoTenantAccess component
  if (user.noTenantAccess) {
    return <NoTenantAccess />;
  }
  
  return children;
};

// Support Role Redirect Component - redirects Support role to support tickets
const SupportRoleRedirect = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  
  const isSupportRole = user?.roles?.some(role => (role.RoleName || role) === 'Support') || 
                       user?.selectedRole?.RoleName === 'Support' || 
                       user?.selectedRole === 'Support';
  
  if (isSupportRole) {
    const allowedPaths = ['/support-tickets', '/knowledge-center', '/admin/entities/knowledge'];
    const isAllowedPath = allowedPaths.some(path => location.pathname.startsWith(path));
    
    if (!isAllowedPath) {
      return <Navigate to="/support-tickets" replace />;
    }
  }
  
  return children;
};

// Admin Protected Route Component
const AdminProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.userType !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Role-based Protected Route Component for Manager/Admin access
const RoleProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user is Support role trying to access restricted routes
  const isSupportRole = user.roles?.some(role => (role.RoleName || role) === 'Support') || 
                       user.selectedRole?.RoleName === 'Support' || 
                       user.selectedRole === 'Support';
  
  if (isSupportRole) {
    // Support role can only access support-tickets and knowledge-center
    const allowedPaths = ['/support-tickets', '/knowledge-center', '/admin/entities/knowledge'];
    const currentPath = location.pathname;
    const isAllowedPath = allowedPaths.some(path => currentPath.startsWith(path));
    
    if (!isAllowedPath) {
      return (
        <Layout>
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="text-6xl text-gray-400 mb-4">🔒</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
              <p className="text-gray-600 mb-4">
                Support role can only access Support Tickets and Knowledge Center.
              </p>
              <button
                onClick={() => window.location.href = '/support-tickets'}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Go to Support Tickets
              </button>
            </div>
          </div>
        </Layout>
      );
    }
  }
  
  // Note: User role access is controlled by backend data governance policies
  // User role can access entities with Policy 2/3, but not Policy 1
  // The backend will enforce these restrictions via data governance service
  
  // Check if user has required role
  let hasRequiredRole = false;
  
  if (requiredRoles.length === 0) {
    hasRequiredRole = true;
  } else {
    // For admin users, check user.role and userType
    if (user.userType === 'admin') {
      hasRequiredRole = requiredRoles.includes(user.role) || 
                       (user.isAdmin && requiredRoles.includes('Admin')) ||
                       (user.isSuperAdmin && requiredRoles.includes('SuperAdmin'));
    } else {
      // For regular users, check user.roles array
      hasRequiredRole = user.roles && user.roles.some(role => requiredRoles.includes(role.RoleName));
    }
  }
  
  if (!hasRequiredRole) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="text-6xl text-gray-400 mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">
              {requiredRoles.length > 0 
                ? `${requiredRoles.join(' or ')} role required.`
                : 'Insufficient permissions.'
              }
            </p>
            <p className="text-sm text-gray-500">
              Please contact your system administrator if you believe you should have access to this page.
            </p>
            <button
              onClick={() => window.history.back()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </Layout>
    );
  }
  
  return children;
};

// Tenant Change Detection Wrapper
const TenantChangeDetector = () => {
  const {
    tenantChanged,
    changedTenantCode,
    originalTenantCode,
    dismissWarning,
    reloadPage
  } = useTenantChangeDetection();

  return (
    <TenantChangeWarning
      visible={tenantChanged}
      originalTenantCode={originalTenantCode}
      changedTenantCode={changedTenantCode}
      onReload={reloadPage}
      onDismiss={dismissWarning}
    />
  );
};

// App Routes Component
const AppRoutes = () => {
  const { user } = useAuth();

  // Function to get redirect path based on user type
  const getDefaultRedirectPath = () => {
    if (!user) return "/login";
    if (user.userType === 'admin') return "/admin";
    
    // Support role should redirect to support tickets
    const isSupportRole = user.roles?.some(role => (role.RoleName || role) === 'Support') || 
                         user.selectedRole?.RoleName === 'Support' || 
                         user.selectedRole === 'Support';
    
    if (isSupportRole) return "/support-tickets";
    
    // Note: User role default redirect remains /dashboard
    // User role access to entities is controlled by backend data governance policies
    
    return "/dashboard";
  };

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        user ? <Navigate to={getDefaultRedirectPath()} replace /> : 
        <PublicLayout><Login /></PublicLayout>
      } />
      <Route path="/register" element={
        user ? <Navigate to={getDefaultRedirectPath()} replace /> : 
        <PublicLayout><Register /></PublicLayout>
      } />
      
      {/* Password Reset Route */}
      <Route path="/reset-password/:token" element={
        <PublicLayout><ResetPassword /></PublicLayout>
      } />
      
      {/* LINE Group Verification Success Route */}
      <Route path="/line-group-verified" element={
        <LineGroupVerified />
      } />
      
      {/* Tenant Selection Route */}
      <Route path="/select-tenant" element={
        user && user.userType === 'regular' ? 
        <TenantSelection /> : 
        <Navigate to={getDefaultRedirectPath()} replace />
      } />
      
      {/* Repository Selection Route (Legacy) */}
      <Route path="/select-repository" element={
        user && user.userType === 'regular' ? 
        <RepositorySelector 
          repositories={user.repositories}
          autoSelectSingle={false}
        /> : 
        <Navigate to={getDefaultRedirectPath()} replace />
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <AdminProtectedRoute>
          <AdminDashboard />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/tenants" element={
        <AdminProtectedRoute>
          <TenantManagement />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/tenants/:tenantCode/ai-config" element={
        <AdminProtectedRoute>
          <TenantConfiguration />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/package-management" element={
        <RoleProtectedRoute requiredRoles={['SuperAdmin']}>
          <PackageManagement />
        </RoleProtectedRoute>
      } />
      <Route path="/admin/tenant-usage-dashboard" element={
        <RoleProtectedRoute requiredRoles={['SuperAdmin']}>
          <TenantUsageDashboard />
        </RoleProtectedRoute>
      } />
      
      <Route path="/email-management" element={
        <RoleProtectedRoute requiredRoles={['Admin', 'Manager']}>
          <Layout><EmailManagement /></Layout>
        </RoleProtectedRoute>
      } />
      
      {/* Redirect old email paths to new Email Management */}
      <Route path="/email-config" element={<Navigate to="/email-management" replace />} />
      <Route path="/email-logs" element={<Navigate to="/email-management" replace />} />

      <Route path="/entity-relations-test" element={
        <RoleProtectedRoute requiredRoles={['Admin', 'Manager']}>
          <Layout><EntityRelationsTest /></Layout>
        </RoleProtectedRoute>
      } />
      
      <Route path="/admin/field-visibility" element={
        <RoleProtectedRoute requiredRoles={['Admin', 'Manager']}>
          <Layout><FieldVisibilityManager /></Layout>
        </RoleProtectedRoute>
      } />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <SupportRoleRedirect>
            <Layout><Dashboard /></Layout>
          </SupportRoleRedirect>
        </ProtectedRoute>
      } />

      <Route path="/dashboard/vis-studio" element={
        <ProtectedRoute>
          <Layout><VisStudio /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/customers" element={
        <ProtectedRoute>
          <Layout><Customers /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/customers/new" element={
        <ProtectedRoute>
          <Layout><CustomerForm mode="create" /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/customers/:id" element={
        <ProtectedRoute>
          <Layout><CustomerDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/customers/:id/edit" element={
        <ProtectedRoute>
          <Layout><CustomerForm mode="edit" /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/leads" element={
        <ProtectedRoute>
          <Layout><Leads /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/leads/new" element={
        <ProtectedRoute>
          <Layout><LeadForm mode="create" /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/leads/:id" element={
        <ProtectedRoute>
          <Layout><LeadDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/leads/:id/edit" element={
        <ProtectedRoute>
          <Layout><LeadForm mode="edit" /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/lead-gen" element={
        <ProtectedRoute>
          <Layout><LeadGen /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/opportunities" element={
        <ProtectedRoute>
          <Layout><Opportunities /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/opportunities/new" element={
        <ProtectedRoute>
          <Layout><OpportunityForm mode="create" /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/opportunities/:id" element={
        <ProtectedRoute>
          <Layout><OpportunityDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/opportunities/:id/edit" element={
        <ProtectedRoute>
          <Layout><OpportunityForm mode="edit" /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/perf-test/opportunities" element={
        <ProtectedRoute>
          <OpportunitiesSingleFetch />
        </ProtectedRoute>
      } />
      
      <Route path="/pipeline" element={
        <ProtectedRoute>
          <Layout><Pipeline /></Layout>
        </ProtectedRoute>
      } />

      {/* Sales Targets */}
      <Route path="/salestargets" element={
        <ProtectedRoute>
          <Layout><SalesTargetList /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/salestargets/new" element={
        <ProtectedRoute>
          <Layout><SalesTargetForm /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/salestargets/dashboard" element={
        <ProtectedRoute>
          <Layout><SalesTargetDashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/salestargets/reports" element={
        <ProtectedRoute>
          <Layout><SalesTargetReports /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/salestargets/:id" element={
        <ProtectedRoute>
          <Layout><SalesTargetDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/salestargets/:id/edit" element={
        <ProtectedRoute>
          <Layout><SalesTargetForm /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/activities" element={
        <ProtectedRoute>
          <Layout><Activities /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/activities/:id" element={
        <ProtectedRoute>
          <Layout><Activities /></Layout>
        </ProtectedRoute>
      } />
      
      
      <Route path="/ai-insights" element={
        <ProtectedRoute>
          <Layout><AIInsights /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/business-card-scanner" element={
        <ProtectedRoute>
          <Layout><BusinessCardScanner /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/attachments" element={
        <ProtectedRoute>
          <Layout><Attachments /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/tags" element={
        <RoleProtectedRoute requiredRoles={['Admin']}>
          <Layout><TagManager /></Layout>
        </RoleProtectedRoute>
      } />
      
      <Route path="/notes" element={
        <ProtectedRoute>
          <Layout><Notes /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/notes/:id" element={
        <ProtectedRoute>
          <Layout><Notes /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout><Settings /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/theme-settings" element={
        <ProtectedRoute>
          <Layout><ThemeSettingsPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout><UserProfile /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/google-calendar-callback" element={
        <ProtectedRoute>
          <GoogleCalendarCallback />
        </ProtectedRoute>
      } />

      <Route path="/ai-management" element={
        <RoleProtectedRoute requiredRoles={['Admin', 'Manager']}>
          <Layout><AIManagement /></Layout>
        </RoleProtectedRoute>
      } />

      <Route path="/admin/ocr-management" element={
        <RoleProtectedRoute requiredRoles={['Admin']}>
          <Layout><OCRManagement /></Layout>
        </RoleProtectedRoute>
      } />

      <Route path="/admin/classification-management" element={
        <RoleProtectedRoute requiredRoles={['Admin']}>
          <Layout><ClassificationManagement /></Layout>
        </RoleProtectedRoute>
      } />

      <Route path="/ai-model-management" element={
        <RoleProtectedRoute requiredRoles={['SuperAdmin']}>
          <Layout><AIModelManagement /></Layout>
        </RoleProtectedRoute>
      } />
      
      {/* Redirect old AI paths to new AI Management */}
      <Route path="/ai-configuration" element={<Navigate to="/ai-management" replace />} />
      <Route path="/rag-management" element={<Navigate to="/ai-management" replace />} />
      <Route path="/chat-log-explorer" element={<Navigate to="/ai-management" replace />} />

      <Route path="/line-management" element={
        <RoleProtectedRoute requiredRoles={['Admin']}>
          <Layout><LineManagement /></Layout>
        </RoleProtectedRoute>
      } />

      <Route path="/automation" element={
        <RoleProtectedRoute requiredRoles={['Admin']}>
          <Layout><Automation /></Layout>
        </RoleProtectedRoute>
      } />

      <Route path="/tenant-settings" element={
        <RoleProtectedRoute requiredRoles={['Admin']}>
          <Layout><TenantSettings /></Layout>
        </RoleProtectedRoute>
      } />

      <Route path="/tenant-entity-config" element={
        <RoleProtectedRoute requiredRoles={['Admin']}>
          <Layout><TenantEntityConfig /></Layout>
        </RoleProtectedRoute>
      } />

      <Route path="/pipeline-studio" element={
        <RoleProtectedRoute requiredRoles={['Admin']}>
          <Layout><PipelineStudio /></Layout>
        </RoleProtectedRoute>
      } />

      <Route path="/data-governance" element={
        <RoleProtectedRoute requiredRoles={['Admin']}>
          <Layout><DataGovernanceManager /></Layout>
        </RoleProtectedRoute>
      } />

      <Route path="/admin/entity-entitlements" element={
        <ProtectedRoute>
          <Layout><EntityEntitlementManager /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/webform-management" element={
        <RoleProtectedRoute requiredRoles={['Admin']}>
          <Layout><WebFormManagement /></Layout>
        </RoleProtectedRoute>
      } />

      <Route path="/admin/custom-fields/:entityName" element={
        <RoleProtectedRoute requiredRoles={['Admin']}>
          <Layout><CustomFieldManager /></Layout>
        </RoleProtectedRoute>
      } />

      <Route path="/selection-values" element={
        <RoleProtectedRoute requiredRoles={['Admin']}>
          <Layout><SelectionValuesManagement /></Layout>
        </RoleProtectedRoute>
      } />

      <Route path="/listing-config" element={
        <RoleProtectedRoute requiredRoles={['Admin']}>
          <Layout><ListingConfigManager /></Layout>
        </RoleProtectedRoute>
      } />


      <Route path="/menu-config" element={
        <RoleProtectedRoute requiredRoles={['Admin']}>
          <Layout><MenuConfigManager /></Layout>
        </RoleProtectedRoute>
      } />

      <Route path="/role-config" element={
        <RoleProtectedRoute requiredRoles={['Admin']}>
          <RoleConfigManager />
        </RoleProtectedRoute>
      } />

      <Route path="/admin/entities/knowledge/:articleId" element={
        <ProtectedRoute>
          <Layout><KnowledgeCenter /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/entities/knowledge" element={
        <ProtectedRoute>
          <Layout><KnowledgeCenter /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/knowledge-center" element={
        <ProtectedRoute>
          <Layout><KnowledgeCenter /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/sales-assist" element={
        <ProtectedRoute>
          <Layout><SalesAssist /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/support-tickets" element={
        <ProtectedRoute>
          <Layout><SupportTickets /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/tooltip-examples" element={
        <ProtectedRoute>
          <Layout><TooltipExamples /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/reports" element={
        <ProtectedRoute>
          <Layout><Reports /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports/new" element={
        <ProtectedRoute>
          <Layout><ReportBuilder mode="create" /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports/:id" element={
        <ProtectedRoute>
          <Layout><ReportViewer /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports/:id/edit" element={
        <ProtectedRoute>
          <Layout><ReportBuilder mode="edit" /></Layout>
        </ProtectedRoute>
      } />

      {/* Visualization Studio Routes */}
      <Route path="/visualization-studio" element={
        <ProtectedRoute>
          <Layout><VisualizationStudio /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/visualization-studio/builder" element={
        <ProtectedRoute>
          <Layout><VisualizationBuilder /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/visualization-studio/builder/:id" element={
        <ProtectedRoute>
          <Layout><VisualizationBuilder /></Layout>
        </ProtectedRoute>
      } />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to={getDefaultRedirectPath()} replace />} />
      <Route path="*" element={<Navigate to={getDefaultRedirectPath()} replace />} />
    </Routes>
  );
};

// Main App Component
function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <NotificationProvider>
                <TutorialProvider>
                  <Router>
                    <div className="App">
                      <TenantChangeDetector />
                      <TenantSwitchCacheClear />
                      <NavigationPerfLogger />
                      <AppRoutes />
                      <Toaster
                        position="top-right"
                        toastOptions={{
                          duration: 4000,
                          className: 'dark:bg-gray-800 dark:text-gray-100',
                          style: {
                            background: 'var(--toast-bg, #363636)',
                            color: 'var(--toast-color, #fff)',
                          },
                        }}
                      />
                    </div>
                  </Router>
                </TutorialProvider>
              </NotificationProvider>
            </AuthProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App; 